import { gql } from '@apollo/client';

// Type definitions
export interface SellerDocumentInput {
  base64_encoded_file: string;
}

export interface SellerDocumentResponse {
  success: boolean;
  message: string;
  document_url: string | null;
}

export interface DocumentUploadResponse {
  success: boolean;
  message: string;
  url: string | null;
  attribute_code: string | null;
}

// GraphQL fragments
const DOCUMENT_UPLOAD_FRAGMENT = gql`
  fragment DocumentUploadFields on SellerDocumentOutput {
    success
    message
    document_url
  }
`;

// Contract PDF upload mutation
export const UPLOAD_CONTRACT_MUTATION = gql`
  mutation UploadSellerContract($input: SellerDocumentInput!) {
    uploadSellerContract(input: $input) {
      ...DocumentUploadFields
    }
  }
  ${DOCUMENT_UPLOAD_FRAGMENT}
`;

// Patente PDF upload mutation
export const UPLOAD_PATENTE_MUTATION = gql`
  mutation UploadSellerPatente($input: SellerDocumentInput!) {
    uploadSellerPatente(input: $input) {
      ...DocumentUploadFields
    }
  }
  ${DOCUMENT_UPLOAD_FRAGMENT}
`;

// RNE PDF upload mutation
export const UPLOAD_RNE_MUTATION = gql`
  mutation UploadSellerRne($input: SellerDocumentInput!) {
    uploadSellerRne(input: $input) {
      ...DocumentUploadFields
    }
  }
  ${DOCUMENT_UPLOAD_FRAGMENT}
`;

// CIN Recto PDF upload mutation
export const UPLOAD_CIN_RECTO_MUTATION = gql`
  mutation UploadSellerCinRecto($input: SellerDocumentInput!) {
    uploadSellerCinRecto(input: $input) {
      ...DocumentUploadFields
    }
  }
  ${DOCUMENT_UPLOAD_FRAGMENT}
`;

// CIN Verso PDF upload mutation
export const UPLOAD_CIN_VERSO_MUTATION = gql`
  mutation UploadSellerCinVerso($input: SellerDocumentInput!) {
    uploadSellerCinVerso(input: $input) {
      ...DocumentUploadFields
    }
  }
  ${DOCUMENT_UPLOAD_FRAGMENT}
`;

// RIB PDF upload mutation
export const UPLOAD_RIB_MUTATION = gql`
  mutation UploadSellerRib($input: SellerDocumentInput!) {
    uploadSellerRib(input: $input) {
      ...DocumentUploadFields
    }
  }
  ${DOCUMENT_UPLOAD_FRAGMENT}
`;

// Helper function to convert File to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64.'));
      }
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Maps a GraphQL mutation response to the expected DocumentUploadResponse format
 * @param response The GraphQL response from the server
 * @param attributeCode The attribute code for the document type
 */
const mapToDocumentUploadResponse = (
  response: SellerDocumentResponse,
  attributeCode: string
): DocumentUploadResponse => {
  return {
    success: response.success,
    message: response.message,
    url: response.document_url,
    attribute_code: attributeCode
  };
};

/**
 * Helper functions for each document type that convert the file to base64
 * and execute the appropriate mutation
 */
export const documentUploadHelpers = {
  // Map of mutation names to attribute codes
  attributeCodes: {
    uploadSellerContract: 'wkmpvr_contract',
    uploadSellerPatente: 'wkmpvr_patente',
    uploadSellerRne: 'wkmpvr_rne',
    uploadSellerCinRecto: 'wkmpvr_cin_recto',
    uploadSellerCinVerso: 'wkmpvr_cin_verso',
    uploadSellerRib: 'wkmpvr_rib'
  },

  /**
   * Generic document upload function that accepts a mutation and file
   * @param mutation The GraphQL mutation to use
   * @param file The PDF file to upload
   * @param mutationName The name of the mutation for attribute code mapping
   * @returns Promise with the upload response
   */
  uploadDocument: async (
    client: any,
    mutation: any,
    file: File,
    mutationName: string
  ): Promise<DocumentUploadResponse> => {
    try {
      // Convert file to base64
      const base64EncodedFile = await fileToBase64(file);
      
      // Execute the mutation
      const response = await client.mutate({
        mutation,
        variables: {
          input: {
            base64_encoded_file: base64EncodedFile
          }
        }
      });
      
      // Extract the result data
      const result = response.data[mutationName];
      
      // Map to the expected response format
      return mapToDocumentUploadResponse(
        result,
        documentUploadHelpers.attributeCodes[mutationName as keyof typeof documentUploadHelpers.attributeCodes]
      );
    } catch (error) {
      console.error(`Error uploading document (${mutationName}):`, error);
      return {
        success: false,
        message: 'An error occurred while uploading the document',
        url: null,
        attribute_code: documentUploadHelpers.attributeCodes[mutationName as keyof typeof documentUploadHelpers.attributeCodes]
      };
    }
  },

  // Helper functions for each document type
  uploadContract: (client: any, file: File) => 
    documentUploadHelpers.uploadDocument(client, UPLOAD_CONTRACT_MUTATION, file, 'uploadSellerContract'),
  
  uploadPatente: (client: any, file: File) => 
    documentUploadHelpers.uploadDocument(client, UPLOAD_PATENTE_MUTATION, file, 'uploadSellerPatente'),
  
  uploadRne: (client: any, file: File) => 
    documentUploadHelpers.uploadDocument(client, UPLOAD_RNE_MUTATION, file, 'uploadSellerRne'),
  
  uploadCinRecto: (client: any, file: File) => 
    documentUploadHelpers.uploadDocument(client, UPLOAD_CIN_RECTO_MUTATION, file, 'uploadSellerCinRecto'),
  
  uploadCinVerso: (client: any, file: File) => 
    documentUploadHelpers.uploadDocument(client, UPLOAD_CIN_VERSO_MUTATION, file, 'uploadSellerCinVerso'),
  
  uploadRib: (client: any, file: File) => 
    documentUploadHelpers.uploadDocument(client, UPLOAD_RIB_MUTATION, file, 'uploadSellerRib')
};
